import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Clock, CheckCircle2, XCircle, AlertCircle, ExternalLink, FileText, Loader2, ReceiptEuro, Truck, Undo2 } from 'lucide-react';
import { fetchMyRMA } from '../../services/rmaService';
import { LoadError } from '../../components/rma/LoadError';
import type { RMARequest } from '../../types';
import { AllegatoRma } from '../../components/rma/AllegatoRma';
import { hrefDocumento, numeroOrigine, type ChiaveDocumento } from '../../components/rma/documentiRma';

const RMA_STATUS: Record<string, { label: string; color: string; icon: any }> = {
  aperta:         { label: 'Aperta',                 color: 'bg-blue-100 text-blue-700',    icon: Clock },
  in_lavorazione: { label: 'In Lavorazione',         color: 'bg-amber-100 text-amber-700',  icon: AlertCircle },
  chiusa:         { label: 'Chiusa',                 color: 'bg-gray-100 text-gray-600',    icon: CheckCircle2 },
};

const MOTIVI_SHORT: Record<string, string> = {
  difetto_stampa: 'Difetto stampa', prodotto_danneggiato: 'Danneggiato',
  qualita_non_conforme: 'Qualità non conforme', articolo_errato_ricevuto: 'Articolo errato',
  articolo_ordinato_erroneamente: 'Ordine erroneo', quantita_errata: 'Qtà errata', altro: 'Altro',
};

const BOTTONE = 'h-9 px-3 rounded-lg flex items-center gap-1.5 bg-[hsl(214,58%,41%)] text-white hover:bg-[hsl(214,58%,46%)] transition-colors text-xs font-medium';

/** Chiave del documento (D.D.T./fattura) di provenienza di un articolo. Il
 * documento comanda: articoli con lo stesso SKU ma provenienti da documenti
 * diversi restano su righe/gruppi distinti, non vengono mai fusi. */
function docOrigine(item: any): ChiaveDocumento | null {
  if (!item.docTipork || !item.docNumdoc) return null;
  return { tipork: item.docTipork, anno: item.docAnno, serie: item.docSerie, numdoc: item.docNumdoc };
}

/** Pagina di dettaglio di una singola pratica RMA (aperta da /area-clienti/rma). */
export default function RMADetailPage() {
  const { rmaId } = useParams<{ rmaId: string }>();
  const navigate = useNavigate();
  const [rma, setRma] = useState<RMARequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchMyRMA(rmaId || '')
      .then(found => { if (!cancelled) setRma(found); })
      .catch((e: any) => {
        if (cancelled) return;
        if (e?.response?.status === 404) setNotFound(true);
        else setError(true);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [rmaId, attempt]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="rma-detail-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento pratica...
      </div>
    );
  }

  if (error) {
    return <LoadError message="Non è stato possibile caricare la pratica." onRetry={() => setAttempt(a => a + 1)} testId="rma-detail-error" />;
  }

  if (notFound || !rma) {
    return (
      <div className="rounded-xl border bg-white p-8 text-center" data-testid="rma-detail-not-found">
        <p className="text-sm text-muted-foreground mb-4">Pratica RMA non trovata.</p>
        <button onClick={() => navigate('/area-clienti/rma')} className="text-sm text-[hsl(214,58%,50%)] hover:underline">Torna all'elenco</button>
      </div>
    );
  }

  const rmaAny: any = rma;
  const st = RMA_STATUS[rmaAny.status];
  const StIcon = st?.icon || Clock;
  const statusOrder = ['aperta', 'in_lavorazione', 'chiusa'];

  return (
    <div data-testid="rma-detail-page">
      <Link to="/area-clienti/rma" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4" data-testid="rma-detail-back">
        <ChevronLeft className="w-4 h-4" /> Torna a RMA / Resi
      </Link>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-semibold tracking-tight font-mono">{rmaAny.id}</h1>
            <span className={`text-[11px] px-2.5 py-1 rounded font-medium flex items-center gap-1 ${st?.color}`} data-testid="rma-detail-status">
              <StIcon className="w-3.5 h-3.5" /> {st?.label}
            </span>
          </div>
          <p className="text-muted-foreground text-sm">Aperta il {rmaAny.date}</p>
        </div>
        {/* Modulo ed Esito si aprono in una nuova scheda; ordine di reso e
            nota di credito nella loro pagina. I Documenti d'origine sono sui
            pezzi: possono essere più d'uno. */}
        <div className="flex items-center gap-2 flex-wrap">
          {rma.erpRma?.numdoc && (
            <Link to={`/area-clienti/rma/${rmaAny.id}/ordine`} data-testid="rma-detail-ordine" className={BOTTONE}>
              <Undo2 className="w-4 h-4" /> Ordine di reso
            </Link>
          )}
          {rmaAny.documentoUrl && (
            <a
              href={rmaAny.documentoUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rma-detail-apri-modulo"
              className={BOTTONE}
            >
              <FileText className="w-4 h-4" /> Modulo RMA <ExternalLink className="w-3 h-3 opacity-80" />
            </a>
          )}
          {rmaAny.documentoEsitoUrl && (
            <a
              href={rmaAny.documentoEsitoUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="rma-detail-apri-esito"
              className={BOTTONE}
            >
              <FileText className="w-4 h-4" /> Esito RMA <ExternalLink className="w-3 h-3 opacity-80" />
            </a>
          )}
          {(rma.noteCredito ?? []).map((n, _i, tutte) => (
            <Link
              key={`${n.anno}-${n.serie}-${n.numdoc}`}
              to={hrefDocumento(n)}
              data-testid="rma-detail-nota-credito-link"
              className={BOTTONE}
            >
              <ReceiptEuro className="w-4 h-4" />
              {tutte.length > 1 ? `Nota di credito ${n.numero}` : 'Nota di credito'}
            </Link>
          ))}
        </div>
      </div>

      {/* La motivazione del cliente è espressa per singolo articolo (vedi
          sotto, per ciascun pezzo), non un'unica motivazione generale per
          l'intera pratica. */}

      {rma.spedizioneReso && (
        <div className="rounded-lg border bg-white p-3 mb-4 text-xs flex items-start gap-2" data-testid="rma-detail-spedizione">
          <Truck className="w-4 h-4 text-[hsl(214,58%,41%)] flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-foreground mb-0.5">
              Spedizione di reso · valore € {rma.spedizioneReso.valore.toFixed(2).replace('.', ',')} (IVA esclusa)
            </div>
            <div className="text-muted-foreground">{rma.spedizioneReso.testo}</div>
          </div>
        </div>
      )}

      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Articoli</div>
      <div className="mb-4">
        {rmaAny.items.map((item: any) => (
          <div key={`${item.sku}-${item.docTipork}-${item.docAnno}-${item.docSerie}-${item.docNumdoc}`} className="rounded-lg border bg-white mb-1.5 overflow-hidden">
            <div className="flex items-center justify-between p-3 text-sm gap-2 flex-wrap">
              <div><span className="font-mono text-[hsl(214,58%,41%)] font-medium">{item.sku}</span> — {item.title}</div>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {docOrigine(item) ? (
                  <Link
                    to={hrefDocumento(docOrigine(item)!)}
                    title="Vai al documento di provenienza"
                    data-testid={`rma-detail-documento-${item.sku}`}
                    className="font-mono text-[hsl(214,58%,41%)] hover:underline"
                  >
                    {numeroOrigine(docOrigine(item)!)}
                  </Link>
                ) : <span className="font-mono">—</span>}
                <span>Qtà: {item.qtyReturned}</span>
              </div>
            </div>
            {(item.units ?? []).map((unit: any, idx: number) => (
              <div key={unit.id} className="px-3 pb-2 text-xs text-muted-foreground border-t bg-[hsl(var(--dtk-canvas))] pt-2" data-testid={`rma-detail-unit-${item.sku}-${idx}`}>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 rounded-full bg-[hsl(214,58%,41%)]/10 text-[hsl(214,58%,41%)] font-bold text-[10px] flex items-center justify-center flex-shrink-0">{idx + 1}</span>
                  <span className="font-medium text-foreground">{MOTIVI_SHORT[unit.motivo] ?? unit.motivo}</span>
                  <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    unit.adminDecision === 'approvata' ? 'bg-emerald-100 text-emerald-700' :
                    unit.adminDecision === 'respinta'  ? 'bg-red-100 text-red-700' :
                    'bg-secondary text-muted-foreground'
                  }`}>
                    {unit.adminDecision === 'approvata' ? 'Approvato' : unit.adminDecision === 'respinta' ? 'Respinto' : 'In attesa'}
                  </span>
                </div>
                {/* Note scritte nel carrello resi: dopo l'invio solo lettura (INT-128). */}
                {unit.descrizione && (
                  <p className="ml-6 mt-1 whitespace-pre-line break-words" data-testid={`rma-detail-unit-note-${item.sku}-${idx}`}>
                    <span className="font-medium text-foreground">Note:</span> {unit.descrizione}
                  </p>
                )}
                {unit.photoUrl && (
                  <div className="ml-6 mt-1.5">
                    <AllegatoRma
                      url={unit.photoUrl}
                      downloadUrl={unit.photoDownloadUrl}
                      mime={unit.photoMime}
                      name={unit.photoName}
                      alt={`Foto del reso, ${item.sku}`}
                      testid={unit.photoMime === 'application/pdf'
                        ? `rma-detail-attachment-pdf-${item.sku}-${idx}`
                        : `rma-detail-attachment-photo-${item.sku}-${idx}`}
                      imgClassName="h-14 w-auto rounded border object-cover"
                      pdfClassName="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border bg-red-50 hover:bg-red-100 text-[11px] font-medium text-red-700 transition-colors"
                      pdfContent={<><FileText className="w-3 h-3" /> {unit.photoName || 'Allegato PDF'}</>}
                    />
                  </div>
                )}

                {/* Esito del pezzo respinto: motivazione + eventuale allegato dell'operatore */}
                {unit.adminDecision === 'respinta' && (unit.adminNote || unit.adminPhotoUrl) && (
                  <div className="ml-6 mt-2 rounded-lg border border-red-200 bg-red-50/60 p-2.5 space-y-1.5" data-testid={`rma-detail-reject-reason-${item.sku}-${idx}`}>
                    {unit.adminNote && (
                      <p className="text-[11px] text-red-800">
                        <span className="font-semibold">Motivo del respingimento:</span> {unit.adminNote}
                      </p>
                    )}
                    {unit.adminPhotoUrl && (
                      <AllegatoRma
                        url={unit.adminPhotoUrl}
                        downloadUrl={unit.adminPhotoDownloadUrl}
                        mime={unit.adminPhotoMime}
                        name={unit.adminPhotoName}
                        alt={`Foto del respingimento, ${item.sku}`}
                        testid={unit.adminPhotoMime === 'application/pdf'
                          ? `rma-detail-reject-attachment-pdf-${item.sku}-${idx}`
                          : `rma-detail-reject-attachment-photo-${item.sku}-${idx}`}
                        imgClassName="h-14 w-auto rounded border object-cover"
                        pdfClassName="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border bg-white hover:bg-red-100 text-[11px] font-medium text-red-700 transition-colors"
                        pdfContent={<><FileText className="w-3 h-3" /> {unit.adminPhotoName || 'Allegato PDF'}</>}
                      />
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Workflow Steps */}
      <div className="rounded-xl border bg-white p-5">
        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Stato Pratica</div>
        <div className="flex items-center gap-2">
          {['aperta', 'in_lavorazione', 'chiusa'].map((s, i) => {
            const isCurrent = rmaAny.status === s;
            const isPast = statusOrder.indexOf(rmaAny.status) > statusOrder.indexOf(s);
            return (
              <React.Fragment key={s}>
                <div className={`text-[11px] px-3 py-1.5 rounded-full font-medium whitespace-nowrap ${
                  isCurrent ? 'bg-[hsl(214,58%,41%)] text-white' : isPast ? 'bg-emerald-100 text-emerald-700' : 'bg-secondary text-muted-foreground'
                }`}>
                  {RMA_STATUS[s]?.label}
                </div>
                {i < 2 && <div className={`flex-1 h-px ${isPast ? 'bg-emerald-300' : 'bg-border'}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
